import React, { useMemo, useState } from "react";
import { useI18n } from "@/i18n";
import { estimatePrice, SIZE_OPTIONS, FINISH_OPTIONS } from "@/lib/pricing";
import { categoryWhatsappLink } from "@/lib/whatsapp";
import { MessageCircle, Calculator } from "lucide-react";

export default function PriceEstimator({ categoryKey, categoryName }) {
  const { t } = useI18n();
  const [size, setSize] = useState("M");
  const [finish, setFinish] = useState("matte");

  const range = useMemo(
    () => estimatePrice(categoryKey, size, finish),
    [categoryKey, size, finish]
  );

  return (
    <section
      data-testid={`price-estimator-${categoryKey}`}
      className="py-16 sm:py-20 border-t border-white/5 bg-black/40"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 grid lg:grid-cols-[1fr_1.1fr] gap-10 items-start">
        <div>
          <div className="font-mono-accent text-xs uppercase tracking-[0.25em] text-brand-blue-light flex items-center gap-2">
            <Calculator size={14} /> {t.estimator.eyebrow}
          </div>
          <h2 className="font-heading mt-3 text-3xl sm:text-4xl font-black tracking-tighter">
            {t.estimator.title}
          </h2>
          <p className="mt-4 text-zinc-400 text-sm leading-relaxed max-w-md">
            {t.estimator.subtitle}
          </p>
        </div>

        <div className="border border-white/10 bg-zinc-900/60 p-6 sm:p-8 space-y-6">
          {/* Size */}
          <div>
            <div className="font-mono-accent text-[11px] uppercase tracking-[0.25em] text-zinc-400 mb-3">
              {t.estimator.size}
            </div>
            <div className="grid grid-cols-3 gap-2">
              {SIZE_OPTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  data-testid={`estimator-size-${s}`}
                  onClick={() => setSize(s)}
                  className={`px-3 py-3 border font-mono-accent uppercase tracking-widest text-[11px] transition-all text-left ${
                    size === s
                      ? "bg-brand-blue/15 border-brand-blue text-brand-blue-light shadow-[0_0_18px_rgba(0,85,255,0.3)]"
                      : "bg-black/40 border-white/10 text-zinc-300 hover:border-white/30"
                  }`}
                >
                  <div className="font-extrabold text-base">{s}</div>
                  <div className="text-[10px] mt-1 opacity-80">
                    {t.estimator.sizes[s]}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Finish */}
          <div>
            <div className="font-mono-accent text-[11px] uppercase tracking-[0.25em] text-zinc-400 mb-3">
              {t.estimator.finish}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {FINISH_OPTIONS.map((f) => (
                <button
                  key={f}
                  type="button"
                  data-testid={`estimator-finish-${f}`}
                  onClick={() => setFinish(f)}
                  className={`px-3 py-3 border font-mono-accent uppercase tracking-widest text-[11px] transition-all ${
                    finish === f
                      ? "bg-brand-blue/15 border-brand-blue text-brand-blue-light shadow-[0_0_18px_rgba(0,85,255,0.3)]"
                      : "bg-black/40 border-white/10 text-zinc-300 hover:border-white/30"
                  }`}
                >
                  {t.estimator.finishes[f]}
                </button>
              ))}
            </div>
          </div>

          {/* Range */}
          <div className="bg-gradient-to-r from-brand-blue/15 via-amber-400/10 to-brand-blue/15 border border-brand-blue/40 p-5">
            <div className="font-mono-accent text-[10px] uppercase tracking-[0.28em] text-brand-blue-light">
              {t.estimator.range_label}
            </div>
            <div
              data-testid={`estimator-range-${categoryKey}`}
              className="font-heading mt-2 text-4xl sm:text-5xl font-black tracking-tighter"
            >
              {range ? `$${range.lo} – $${range.hi}` : "—"}
            </div>
            <div className="mt-3 text-[11px] font-mono-accent text-zinc-400 italic">
              {t.estimator.disclaimer}
            </div>
          </div>

          <a
            href={categoryWhatsappLink(categoryName)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`estimator-whatsapp-${categoryKey}`}
            className="inline-flex items-center justify-center gap-2 w-full bg-emerald-500 hover:bg-emerald-400 text-black px-5 py-3.5 uppercase tracking-widest text-xs font-bold border border-emerald-300/60 transition-all font-mono-accent"
          >
            <MessageCircle size={14} />
            {t.estimator.confirm_cta}
          </a>
        </div>
      </div>
    </section>
  );
}
